import { useState, useRef } from 'react'
import * as React from 'react'
import { CONFIG } from 'shared/constants'
import classes from './MadLibInputs.styles.scss'

const DEFAULT_MAX_LENGTH = 30
const WARN_AT_CHARACTERS_REMAINING = 15

export interface InputConfig {
  affinity?: 'loves' | 'needs' | 'please_no'
  type: 'text' | 'colorpicker'
  helpText?: string
  maxLength?: number
}

interface MadLibProps {
  structure: (props: { [key: string]: JSX.Element }) => JSX.Element
  inputs: { [key: string]: InputConfig }
  initialValues: { [key: string]: string }
  onValuesChange: (values: { [key: string]: string }) => void
}

export const MadLibInputs = ({
  structure,
  inputs,
  initialValues,
  onValuesChange,
}: MadLibProps) => {
  const [values, setValues] = useState(initialValues)
  const [remainingChars, setRemainingChars] = useState<{
    [key: string]: number
  }>({})
  const inputsRef = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const handleChange = (event: React.FocusEvent<HTMLDivElement>) => {
    const inputKey = event.target.getAttribute('data-input-key') as string
    const newValues = {
      ...values,
      [inputKey]: event.target.innerText,
    }
    setValues(newValues)
    onValuesChange(newValues)
  }

  /**
   * Handle the allowed maximum length of the input in a contentEditable div.
   *
   * The maxLength attribute is not natively supported for contentEditable div elements,
   * unlike with traditional input elements. Therefore, we need to manually implement
   * this functionality.
   *
   * When the text is trimmed, the cursor might unexpectedly jump to the beginning of
   * the div. To prevent this, we use the Range and Selection APIs to place the
   * cursor at the end of the div after trimming.
   */
  const handleMaxLength = (
    event: React.FormEvent<HTMLDivElement>,
    inputKey: string
  ) => {
    const inputConfig = inputs[inputKey]
    const maxLength = inputConfig.maxLength || DEFAULT_MAX_LENGTH
    let currentText = event.currentTarget.textContent || ''

    // If the text exceeds the maximum length, trim it
    if (currentText.length > maxLength) {
      // Trim the text if it exceeds max length
      currentText = currentText.slice(0, maxLength)
      event.currentTarget.textContent = currentText

      // Create a range and set the cursor at the end of the div
      const range = document.createRange()
      const selection = window.getSelection()
      if (selection) {
        // Check if selection is not null
        range.selectNodeContents(event.currentTarget)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    }
  }

  // Calculates the remaining characters from an inputs maxLength config
  const handleRemainingChars = (
    event: React.FormEvent<HTMLDivElement>,
    inputKey: string
  ) => {
    const inputConfig = inputs[inputKey]
    const currentText = event.currentTarget.textContent || ''
    const remaining =
      (inputConfig.maxLength || DEFAULT_MAX_LENGTH) - currentText.length
    setRemainingChars({ ...remainingChars, [inputKey]: remaining })
  }

  const handleInput = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const inputKey = event.currentTarget.getAttribute(
      'data-input-key'
    ) as string

    handleMaxLength(event, inputKey)
    handleRemainingChars(event, inputKey)
  }

  // TODO: Implement a better color picker with a color swatch in it
  const renderColorPicker = (
    inputKey: string,
    helpText: string | undefined
  ) => (
    <div
      key={inputKey}
      style={{ display: 'inline-block', position: 'relative' }}
    >
      <select
        className={classes.colorPicker}
        data-input-key={inputKey}
        value={values[inputKey]}
        onChange={(event) =>
          setValues({ ...values, [inputKey]: event.target.value })
        }
      >
        <option selected aria-label="Select their favorite color" value="" />
        {CONFIG.registryAttributeDataOptions?.favoriteColorOptions.map(
          (colorOption) => (
            <option key={colorOption.name} value={colorOption.name}>
              {colorOption.name}
            </option>
          )
        )}
      </select>
      {helpText && (
        <span
          className={classes.helpText}
          dangerouslySetInnerHTML={{ __html: helpText }}
        />
      )}
    </div>
  )

  // A contentEditable div is used instead of a regular input because it allows for a
  // wider range of customization options.
  const renderTextInput = (inputKey: string, helpText: string | undefined) => (
    <div
      key={inputKey}
      style={{ display: 'inline-block', position: 'relative', maxWidth: '95%' }}
    >
      <div
        contentEditable
        suppressContentEditableWarning
        className={classes.input}
        data-input-key={inputKey}
        ref={(ref) => {
          inputsRef.current[inputKey] = ref
          return null
        }}
        onBlur={handleChange}
        onInput={handleInput}
      >
        {values[inputKey]}
      </div>
      {helpText && (
        <span
          className={classes.helpText}
          dangerouslySetInnerHTML={{ __html: helpText }}
        />
      )}
    </div>
  )

  const renderInputByType = (inputKey: string, config: InputConfig) => {
    const { type, helpText } = config
    const remaining = remainingChars[inputKey]
    const maxLength = config.maxLength || DEFAULT_MAX_LENGTH
    let updatedHelpText = `<span> ${helpText} </span>`

    if (
      remaining !== undefined &&
      remaining <= WARN_AT_CHARACTERS_REMAINING &&
      remaining >= 0
    ) {
      updatedHelpText += ` <span>${remaining}/${maxLength}</span>`
    }

    return type === 'colorpicker'
      ? renderColorPicker(inputKey, updatedHelpText)
      : renderTextInput(inputKey, updatedHelpText)
  }

  const inputElements = Object.entries(inputs).reduce(
    (acc, [key, config]) => {
      acc[key] = renderInputByType(key, config)
      return acc
    },
    {} as { [key: string]: JSX.Element }
  )

  return <div>{structure(inputElements)}</div>
}
